import type { AuctionSession } from '@carsfinders/contracts';

import { t } from '../../../../lib/i18n';
import { cn } from '../../utils';
import { pluralSuffix } from './session-schedule-utils';

/**
 * The four counters across the top of the session schedule — admin port of
 * `apps/auction/.../components/ScheduleStatCards.tsx`. Unaffected by the
 * `myBids` omission elsewhere in this port: none of these four counters ever
 * depended on bid state.
 *
 * Since 2026-10-12 the feed is `scope=bids`, so every counter here counts lots
 * WITH CLIENT BIDS, not the catalogue — the first two cards are relabelled to
 * say so; «сьогодні» / «цього тижня» read correctly either way.
 */

interface SessionStatCardsProps {
  /** «Вересень 2026» — the month the first card counts. */
  monthLabel: string;
  /** Sessions whose `date` falls in the month the calendar is showing. */
  sessionsThisMonth: number;
  /** Lots in sessions that have not closed yet, over the fetched window. */
  activeLots: number;
  /** Lots closing today, in the display zone. */
  lotsToday: number;
  /** Earliest session still to close today, for the third card's subtitle. */
  nextSessionToday: AuctionSession | null;
  lotsThisWeek: number;
  sessionsThisWeek: number;
  onShowList: () => void;
  onShowToday: () => void;
  onShowTimeline: () => void;
}

interface StatCard {
  key: string;
  label: string;
  value: number;
  sub: string;
  highlighted: boolean;
  onClick: () => void;
}

export function SessionStatCards({
  monthLabel,
  sessionsThisMonth,
  activeLots,
  lotsToday,
  nextSessionToday,
  lotsThisWeek,
  sessionsThisWeek,
  onShowList,
  onShowToday,
  onShowTimeline,
}: SessionStatCardsProps) {
  const sessionsWord = (count: number) => t(`auctionSchedule.units.sessions.${pluralSuffix(count)}`);

  const cards: StatCard[] = [
    {
      key: 'month',
      label: t('auctionSchedule.stats.inMonthBids'),
      value: sessionsThisMonth,
      sub: t('auctionSchedule.stats.inMonthSub', { month: monthLabel }),
      highlighted: false,
      onClick: onShowList,
    },
    {
      key: 'active',
      label: t('auctionSchedule.stats.activeBidLots'),
      value: activeLots,
      sub: t('auctionSchedule.stats.activeBidLotsSub'),
      highlighted: false,
      onClick: onShowList,
    },
    {
      key: 'today',
      label: t('auctionSchedule.stats.closingToday'),
      value: lotsToday,
      sub: nextSessionToday
        ? t('auctionSchedule.stats.closingTodaySub', {
            house: nextSessionToday.house.shortName,
            time: nextSessionToday.time,
          })
        : t('auctionSchedule.stats.closingTodayNone'),
      highlighted: true,
      onClick: onShowToday,
    },
    {
      key: 'week',
      label: t('auctionSchedule.stats.thisWeek'),
      value: lotsThisWeek,
      sub: t('auctionSchedule.stats.thisWeekSub', {
        sessions: `${sessionsThisWeek} ${sessionsWord(sessionsThisWeek)}`,
      }),
      highlighted: false,
      onClick: onShowTimeline,
    },
  ];

  return (
    <div className="grid grid-cols-[repeat(auto-fit,minmax(200px,1fr))] gap-3">
      {cards.map((card) => (
        <button
          key={card.key}
          type="button"
          onClick={card.onClick}
          className={cn(
            'cursor-pointer rounded-cf-lg border border-cf-line bg-cf-surface px-[18px] py-4 text-left',
            card.highlighted
              ? 'shadow-[inset_0_3px_0_var(--color-cf-accent)]'
              : 'shadow-[inset_0_3px_0_transparent]',
          )}
        >
          <p className="font-mono text-[10px] tracking-[0.14em] text-cf-ink-subtle uppercase">{card.label}</p>
          <p
            className={cn(
              'mt-2.5 font-mono text-[30px] leading-[30px] font-medium',
              card.highlighted ? 'text-cf-ok' : 'text-cf-ink',
            )}
          >
            {card.value}
          </p>
          <p className="mt-1.5 text-[12px] text-cf-ink-subtle">{card.sub}</p>
        </button>
      ))}
    </div>
  );
}
